import React, { useState, useEffect, useCallback } from 'react';
import { Server, Cpu, HardDrive, Network, Info, Activity, Database, Clock, Globe, Package, RefreshCw, Loader2 } from 'lucide-react';

const SystemInfo = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [systemResources, setSystemResources] = useState({
    cpu: { percent: 0, cores: 0 },
    memory: { percent: 0, used_gb: 0, total_gb: 0 },
    disk: { percent: 0, used_gb: 0, total_gb: 0 }
  });

  const [serverInfo, setServerInfo] = useState({
    hostname: 'Chargement...',
    ip_local: '...',
    ip_public: '...',
    os: '...',
    kernel: '...',
    uptime: '...',
    timezone: '...'
  });

  const [sylinkInfo, setSylinkInfo] = useState({
    version: '...',
    build: '...',
    license: '...',
    mode: '...',
    installation_path: '...'
  });

  const [components, setComponents] = useState({
    backend: { version: 'FastAPI', status: 'Checking...', port: 5001 },
    frontend: { version: 'React', status: 'Running', port: 3000 },
    database: { version: 'PostgreSQL', status: 'Checking...', port: 5432 },
    cache: { version: 'Redis', status: 'Checking...', port: 6379 },
    websocket: { version: 'WebSocket', status: 'Checking...', connections: 0 }
  });

  const [tools, setTools] = useState({
    docker: [],
    cli: []
  });

  const [stats, setStats] = useState({
    tools_adapters: 0,
    ai_models: 0,
    docker_containers: 0,
    integrations: 0
  });

  const [networkInfo, setNetworkInfo] = useState({
    interface: 'eth0',
    mac: '00:00:00:00:00:00',
    dns: 'N/D',
    gateway: 'N/D',
    subnet: 'N/D'
  });

  // Fetch system info from API
  const fetchSystemInfo = useCallback(async (abortSignal) => {
    // Create internal timeout that aborts if external signal not provided
    const internalController = new AbortController();
    const signal = abortSignal || internalController.signal;
    const timeoutId = setTimeout(() => internalController.abort(), 10000); // 10 second timeout

    try {
      // Check if already aborted before starting
      if (signal.aborted) {
        return;
      }

      const token = localStorage.getItem('access_token');
      const response = await fetch('/api/v1/system/info', {
        headers: { 'Authorization': `Bearer ${token}` },
        signal: signal
      });

      // Check if aborted after fetch
      if (signal.aborted) {
        return;
      }

      if (response.ok) {
        const data = await response.json();

        // Check if aborted after parsing
        if (signal.aborted) {
          return;
        }

        // Update server info
        if (data.server) {
          setServerInfo({
            hostname: data.server.hostname || 'N/D',
            ip_local: data.server.ip_local || 'N/D',
            ip_public: data.server.ip_public || 'N/D',
            os: data.server.os || 'N/D',
            kernel: data.server.kernel || 'N/D',
            uptime: data.server.uptime || 'N/D',
            timezone: data.server.timezone || 'N/D'
          });
        }

        // Update SYLINK info
        if (data.sylink) {
          setSylinkInfo({
            version: data.sylink.version || 'N/D',
            build: data.sylink.build || 'N/D',
            license: data.sylink.license || 'N/D',
            mode: data.sylink.mode || 'N/D',
            installation_path: data.sylink.installation_path || 'N/D'
          });
        }

        // Update components
        if (data.components) {
          setComponents(data.components);
        }

        // Update tools
        if (data.tools) {
          setTools(data.tools);
        }

        // Update stats
        if (data.stats) {
          setStats(data.stats);
        }

        // Update network info
        if (data.network) {
          setNetworkInfo(data.network);
        }

        // Clear error on success
        setError(null);
      } else if (response.status === 401) {
        // Unauthorized - token may be expired
        if (!signal.aborted) {
          setError('Session expirée. Veuillez vous reconnecter.');
        }
      } else {
        // Other HTTP errors
        if (!signal.aborted) {
          setError(`Erreur serveur (${response.status})`);
        }
      }
    } catch (error) {
      // Don't update state if aborted (component unmounting)
      if (signal.aborted) {
        return;
      }

      if (error.name === 'AbortError') {
        // Timeout - show retry message
        setError('Le chargement a pris trop de temps. Cliquez sur Actualiser pour réessayer.');
      } else if (error.name === 'TypeError' && error.message === 'Failed to fetch') {
        // Network error - silent handling, keep existing data or show generic message on first load
        setError(prev => prev ? prev : null);
      } else {
        // Other unexpected errors - log only in development
        if (process.env.NODE_ENV === 'development') {
          console.warn('System info fetch error:', error);
        }
        setError('Erreur lors du chargement des informations système');
      }
    } finally {
      clearTimeout(timeoutId);
      // Only update loading if not aborted
      if (!signal.aborted) {
        setLoading(false);
      }
    }
  }, []);

  // Fetch resources in real-time
  const fetchResources = useCallback(async (abortSignal) => {
    try {
      // Check if aborted before starting
      if (abortSignal?.aborted) {
        return;
      }

      const token = localStorage.getItem('access_token');
      const response = await fetch('/api/v1/system/resources', {
        headers: { 'Authorization': `Bearer ${token}` },
        signal: abortSignal
      });

      // Check if aborted after fetch
      if (abortSignal?.aborted) {
        return;
      }

      if (response.ok) {
        const data = await response.json();

        // Check if aborted after parsing
        if (abortSignal?.aborted) {
          return;
        }

        setSystemResources({
          cpu: { percent: data.cpu?.percent || 0, cores: data.cpu?.cores || 0 },
          memory: {
            percent: data.memory?.percent || 0,
            used_gb: data.memory?.used_gb || 0,
            total_gb: data.memory?.total_gb || 0
          },
          disk: {
            percent: data.disk?.percent || 0,
            used_gb: data.disk?.used_gb || 0,
            total_gb: data.disk?.total_gb || 0
          }
        });
      }
    } catch (error) {
      // Don't log abort errors (expected on cleanup)
      if (error.name === 'AbortError') {
        return;
      }
      // Silent handling for network errors - resources will be refreshed on next interval
      if (process.env.NODE_ENV === 'development' && !(error.name === 'TypeError' && error.message === 'Failed to fetch')) {
        console.warn('Resources fetch error:', error);
      }
    }
  }, []);

  useEffect(() => {
    // Create AbortController for cleanup
    const controller = new AbortController();

    // Initial fetch with abort signal
    fetchSystemInfo(controller.signal);
    fetchResources(controller.signal);

    // Refresh resources every 5 seconds
    const resourceInterval = setInterval(() => {
      if (!controller.signal.aborted) {
        fetchResources(controller.signal);
      }
    }, 5000);

    // Refresh system info every 30 seconds
    const infoInterval = setInterval(() => {
      if (!controller.signal.aborted) {
        fetchSystemInfo(controller.signal);
      }
    }, 30000);

    return () => {
      // Abort all pending requests
      controller.abort();
      clearInterval(resourceInterval);
      clearInterval(infoInterval);
    };
  }, [fetchSystemInfo, fetchResources]);

  const ResourceBar = ({ label, percent, details, unavailable }) => (
    <div className="min-w-0 overflow-hidden">
      <div className="flex items-center justify-between gap-2 mb-2 min-w-0">
        <span className="text-sm text-gray-400 truncate min-w-0">{label}</span>
        <span className={`text-sm font-bold flex-shrink-0 whitespace-nowrap ${unavailable ? 'text-gray-500' : 'text-brand-green'}`}>
          {unavailable ? 'N/D' : `${Math.round(percent)}%`}
        </span>
      </div>
      <div className="w-full bg-gray-800 rounded-full h-2">
        {!unavailable && (
          <div
            className={`h-full rounded-full transition-all ${
              percent > 80 ? 'bg-red-500' : percent > 60 ? 'bg-yellow-500' : 'bg-brand-green'
            }`}
            style={{ width: `${percent}%` }}
          ></div>
        )}
      </div>
      {details && <div className={`text-xs mt-1 truncate ${unavailable ? 'text-gray-600' : 'text-gray-500'}`}>{details}</div>}
    </div>
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 text-brand-green animate-spin" />
        <span className="ml-3 text-gray-400">Chargement des informations système…</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center py-12 space-y-4">
        <div className="text-yellow-500 text-center">{error}</div>
        <button
          onClick={() => { setError(null); setLoading(true); fetchSystemInfo(); }}
          className="px-4 py-2 bg-brand-green/20 text-brand-green rounded-lg hover:bg-brand-green/30 flex items-center gap-2"
        >
          <RefreshCw className="w-4 h-4" />
          Reessayer
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Refresh button */}
      <div className="flex justify-end">
        <button
          onClick={() => { setLoading(true); fetchSystemInfo(); }}
          className="px-3 py-2 bg-brand-green/20 text-brand-green rounded-lg hover:bg-brand-green/30 flex items-center gap-2 text-sm"
        >
          <RefreshCw className="w-4 h-4" />
          Actualiser
        </button>
      </div>

      {/* Informations Serveur */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Serveur */}
        <div className="bg-brand-black rounded-lg p-5 border border-brand-green/30">
          <div className="flex items-center gap-2 mb-4">
            <Server className="w-5 h-5 text-brand-green" />
            <h3 className="font-bold text-white">Informations Serveur</h3>
          </div>
          <div className="space-y-3 text-sm">
            <div className="flex justify-between">
              <span className="text-gray-400">Nom d'hôte :</span>
              <span className="text-white font-mono">{serverInfo.hostname}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-400">IP Locale :</span>
              <span className="text-brand-green font-mono">{serverInfo.ip_local}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-400">IP Publique :</span>
              <span className="text-brand-green font-mono">{serverInfo.ip_public}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-400">OS :</span>
              <span className="text-white">{serverInfo.os}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-400">Noyau :</span>
              <span className="text-white">{serverInfo.kernel}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-400">Temps d'activité :</span>
              <span className="text-white">{serverInfo.uptime}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-400">Fuseau horaire :</span>
              <span className="text-white">{serverInfo.timezone}</span>
            </div>
          </div>
        </div>

        {/* SYLINK Info */}
        <div className="bg-brand-black rounded-lg p-5 border border-brand-green/30">
          <div className="flex items-center gap-2 mb-4">
            <Info className="w-5 h-5 text-brand-green" />
            <h3 className="font-bold text-white">Plateforme SYLINK</h3>
          </div>
          <div className="space-y-3 text-sm">
            <div className="flex justify-between">
              <span className="text-gray-400">Version :</span>
              <span className="text-brand-green font-bold">{sylinkInfo.version}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-400">Build :</span>
              <span className="text-white">{sylinkInfo.build}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-400">Licence :</span>
              <span className="text-brand-green font-mono text-xs">{sylinkInfo.license}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-400">Mode :</span>
              <span className="text-white">{sylinkInfo.mode}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-400">Installation :</span>
              <span className="text-white text-xs">{sylinkInfo.installation_path}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Ressources Système */}
      <div className="bg-brand-black rounded-lg p-5 border border-brand-green/30">
        <div className="flex items-center gap-2 mb-4">
          <Activity className="w-5 h-5 text-brand-green" />
          <h3 className="font-bold text-white">Ressources Système (Temps Réel)</h3>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <ResourceBar
            label="CPU"
            percent={systemResources.cpu?.percent || 0}
            details={systemResources.cpu?.cores > 0 ? `${systemResources.cpu?.cores} cœurs` : 'Serveur hors ligne'}
            unavailable={!systemResources.cpu?.cores}
          />
          <ResourceBar
            label="RAM"
            percent={systemResources.memory?.percent || 0}
            details={systemResources.memory?.total_gb > 0 ? `${(systemResources.memory?.used_gb || 0).toFixed(1)}GB / ${(systemResources.memory?.total_gb || 0).toFixed(1)}GB` : 'Serveur hors ligne'}
            unavailable={!systemResources.memory?.total_gb}
          />
          <ResourceBar
            label="Disque"
            percent={systemResources.disk?.percent || 0}
            details={systemResources.disk?.total_gb > 0 ? `${Math.round(systemResources.disk?.used_gb || 0)}GB / ${Math.round(systemResources.disk?.total_gb || 0)}GB` : 'Serveur hors ligne'}
            unavailable={!systemResources.disk?.total_gb}
          />
        </div>
      </div>

      {/* Composants */}
      <div className="bg-brand-black rounded-lg p-5 border border-brand-green/30">
        <div className="flex items-center gap-2 mb-4">
          <Package className="w-5 h-5 text-brand-green" />
          <h3 className="font-bold text-white">Composants Principaux</h3>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {Object.entries(components).map(([key, comp]) => {
            const componentLabels = {
              backend: 'Serveur API',
              frontend: 'Interface Web',
              database: 'Base de données',
              cache: 'Mémoire cache',
              websocket: 'Connexion temps réel'
            };
            return (
            <div key={key} className="flex items-center justify-between bg-brand-black-lighter p-3 rounded">
              <div>
                <div className="text-white font-medium">{componentLabels[key] || key}</div>
                <div className="text-xs text-gray-500">{comp.version}</div>
              </div>

              <div className="flex items-center gap-2">
                <span className="text-xs text-gray-400">Port {comp.port}</span>
                <div className={`w-2 h-2 rounded-full ${comp.status === 'Running' || comp.status === 'Active' ? 'bg-green-500' : 'bg-red-500'}`}></div>
              </div>
            </div>
          );
          })}
        </div>
      </div>

      {/* Outils */}
      <div className="bg-brand-black rounded-lg p-5 border border-brand-green/30">
        <div className="flex items-center gap-2 mb-4">
          <Globe className="w-5 h-5 text-brand-green" />
          <h3 className="font-bold text-white">Outils Pentest</h3>
        </div>

        {/* Docker */}
        <div className="mb-4">
          <h4 className="text-sm font-bold text-gray-400 mb-3">Conteneurs Docker</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {tools.docker && tools.docker.length > 0 ? (
              tools.docker.map((tool, i) => (
                <div key={i} className="flex items-center justify-between bg-brand-black-lighter p-3 rounded">
                  <div>
                    <div className="text-white font-medium">{tool.name}</div>
                    <div className="text-xs text-gray-500">
                      {tool.port !== '-' ? `Port ${tool.port}` : 'CLI uniquement'}
                    </div>
                  </div>
                  <div className={`w-2 h-2 rounded-full ${tool.status === 'Running' || tool.status === 'En cours' ? 'bg-green-500 animate-pulse' : 'bg-red-500'}`}></div>
                </div>
              ))
            ) : (
              <div className="text-gray-500 text-sm col-span-2">Aucun conteneur Docker detecte</div>
            )}
          </div>
        </div>

        {/* CLI */}
        <div>
          <h4 className="text-sm font-bold text-gray-400 mb-3">Outils CLI ({tools.cli?.length || 0} installés)</h4>
          <div className="flex flex-wrap gap-2">
            {tools.cli && tools.cli.length > 0 ? (
              tools.cli.map((tool, i) => (
                <span key={i} className="px-3 py-1 bg-brand-green/10 text-brand-green text-xs rounded-full border border-brand-green/30">
                  {tool}
                </span>
              ))
            ) : (
              <span className="text-gray-500 text-sm">Aucun outil CLI detecte</span>
            )}
          </div>
        </div>
      </div>

      {/* Réseau */}
      <div className="bg-brand-black rounded-lg p-5 border border-brand-green/30">
        <div className="flex items-center gap-2 mb-4">
          <Network className="w-5 h-5 text-brand-green" />
          <h3 className="font-bold text-white">Configuration Réseau</h3>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
          <div>
            <span className="text-gray-400">Interface réseau :</span>
            <div className="text-white">{networkInfo.interface}</div>
          </div>
          <div>
            <span className="text-gray-400">MAC :</span>
            <div className="text-white font-mono text-xs">{networkInfo.mac}</div>
          </div>
          <div>
            <span className="text-gray-400">DNS :</span>
            <div className="text-white">{networkInfo.dns}</div>
          </div>
          <div>
            <span className="text-gray-400">Passerelle :</span>
            <div className="text-white">{networkInfo.gateway}</div>
          </div>
          <div>
            <span className="text-gray-400">Sous-réseau :</span>
            <div className="text-white">{networkInfo.subnet}</div>
          </div>
          <div>
            <span className="text-gray-400">DHCP :</span>
            <div className="text-white">Auto</div>
          </div>
        </div>
      </div>

      {/* Bases de Données */}
      <div className="bg-brand-black rounded-lg p-5 border border-brand-green/30">
        <div className="flex items-center gap-2 mb-4">
          <Database className="w-5 h-5 text-brand-green" />
          <h3 className="font-bold text-white">Bases de Données</h3>
        </div>
        <div className="space-y-3">
          <div className="flex items-center justify-between bg-brand-black-lighter p-3 rounded">
            <div>
              <div className="text-white font-medium">PostgreSQL</div>
              <div className="text-xs text-gray-500">sylink_pentest (principal)</div>
            </div>
            <div className="flex items-center gap-3">
              <span className="text-xs text-gray-400">Port 5432</span>
              <div className={`w-2 h-2 rounded-full ${components.database?.status === 'Running' ? 'bg-green-500' : 'bg-red-500'}`}></div>
            </div>
          </div>
        </div>
      </div>

      {/* Stats Globales */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-brand-black rounded-lg p-4 border border-brand-green/30 text-center">
          <div className="text-3xl font-bold text-brand-green mb-1">{stats.tools_adapters || 0}+</div>
          <div className="text-sm text-gray-400">Adaptateurs Outils</div>
        </div>
        <div className="bg-brand-black rounded-lg p-4 border border-brand-green/30 text-center">
          <div className="text-3xl font-bold text-brand-green mb-1">{stats.ai_models || 0}</div>
          <div className="text-sm text-gray-400">Modèles IA</div>
        </div>
        <div className="bg-brand-black rounded-lg p-4 border border-brand-green/30 text-center">
          <div className="text-3xl font-bold text-brand-green mb-1">{stats.docker_containers || 0}</div>
          <div className="text-sm text-gray-400">Conteneurs Docker</div>
        </div>
        <div className="bg-brand-black rounded-lg p-4 border border-brand-green/30 text-center">
          <div className="text-3xl font-bold text-brand-green mb-1">{stats.integrations || 0}</div>
          <div className="text-sm text-gray-400">Intégrations</div>
        </div>
      </div>
    </div>
  );
};

export default SystemInfo;
